import type {ReactElement} from 'react';
import {useRef} from 'react';
import {updateImagePopUpState} from '@Components/poster-editor/components/image-item-popup/image-item-popup-slice';
import useOutsideClick from '@Hooks/useOutsideClick';
import {openMediaSource} from '@Components/poster-editor/library/poster-editor-open-modals';
import {CUSTOM_REPLACE_CONTROL_ID} from '@Components/poster-editor/components/custom-item-action-bar/custom-item-action-bar.types';
import type {ReplaceSourceSelection} from '@Components/media-source-menu/media-source-menu.types';
import {MediaSourceIntent} from '@Components/media-source-menu/media-source-menu.types';
import {getReplaceMediaKindForItem} from '@Components/media-source-menu/media-source-menu.library';
import {ReplaceMediaControls} from '@Components/replace-media-controls';
import {usePopupPosition} from '@Components/poster-editor/components/poster-popup-menu/poster-popup-menu.hooks';
import popupMenuStyles from '@Components/poster-editor/components/poster-popup-menu/poster-popup-menu.module.scss';
import {onReplaceMedia} from '@PosterWhiteboard/page/replace-item.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useAppDispatch, useAppSelector} from '@/hooks';

/** The replace menu the action bar's Replace button opens: the same Uploads / Explore media / AI list as the side panel's Replace, dropping from the button. */
export function ImageItemPopup(): ReactElement | null {
  const popupState = useAppSelector((state) => {
    return state.imageItemPopUpMenu;
  });
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const popUpRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const closePopup = (): void => {
    dispatch(updateImagePopUpState({openPopup: false, anchorRect: null}));
  };

  useOutsideClick(popUpRef, (event?: MouseEvent): void => {
    const target = event?.target as HTMLElement | null;
    if (!popupState.openPopup || target?.closest(`#${CUSTOM_REPLACE_CONTROL_ID}`)) {
      return;
    }
    closePopup();
  });

  usePopupPosition(popUpRef, popupState.anchorRect);

  if (!popupState.openPopup || !activeItem || !(activeItem.isImage() || activeItem.isVideo())) {
    return null;
  }

  const onReplaceOption = (selection: ReplaceSourceSelection): void => {
    closePopup();
    openMediaSource({intent: MediaSourceIntent.REPLACE_ITEM, selection, item: activeItem, onReplace: onReplaceMedia});
  };

  return (
    <div ref={popUpRef} className={`spacing-p-2 ${popupMenuStyles.canvasPopupMenu} ${popupMenuStyles.anchoredMenu}`}>
      <ReplaceMediaControls mediaKind={getReplaceMediaKindForItem(activeItem)} onSelect={onReplaceOption} />
    </div>
  );
}
